import {
  codeableConcept,
  systemFromCodeableConcept,
} from '@seen/core/codeableConcept'
import * as codeSystems from '@seen/core/codeSystems'
import { AssessmentTemplate } from '@seen/core/models/assessmentTemplate'
import {
  isDroppableEmptySection,
  type LibrarySection,
} from '@seen/core/models/compositionLibraryEncoded'
import { IncidentTemplate } from '@seen/core/models/incidentTemplate'
import { ExtendedLibrary } from '@seen/core/models/library'
import { NoteTemplate } from '@seen/core/models/noteTemplate'
import { OrderTemplate } from '@seen/core/models/orderTemplate'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { generateUUID } from '@seen/core/utils'
import { Button, cn, TypographySubtle } from '@seen/ui'
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronLeft,
  IconChevronUp,
  IconHistory,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react'
import type { UseMutationResult } from '@tanstack/react-query'
import type { Editor } from '@tiptap/react'
import {
  type Dispatch,
  type JSX,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router'
import { z } from 'zod'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { H1Editor } from '#src/components/core/H1Editor.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { NoteLabelComboboxButton } from '#src/components/NoteLabelComboboxButton.tsx'
import { partitionLabelSelection } from '#src/components/noteTemplateLabelSelection.ts'
import { createChoiceFieldIds } from '#src/components/rich-text-editor/choice/ChoiceNode.tsx'
import { NodeConfigPanelSlotContext } from '#src/components/rich-text-editor/conditional-display/NodeConfigPanelSlot.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { TemplateDeleteDropdownMenuItem } from '#src/components/TemplateDeleteDropdownMenuItem.tsx'
import { TemplateHistoryDialog } from '#src/components/TemplateHistoryDialog.tsx'
import { ValidatedToolSectionPreview } from '#src/components/ValidatedToolSectionPreview.tsx'
import {
  type UsePatchMutationProxy,
  usePatchMutation,
} from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

const listFormatter = new Intl.ListFormat('en', {
  style: 'long',
  type: 'conjunction',
})

function emptySectionPlaceholder(resourceNounPlural: string): string {
  return `Empty section — ${resourceNounPlural} cannot be created from this template until this is filled or removed.`
}

function pluralResourceNoun(
  template: AssessmentTemplate | OrderTemplate | IncidentTemplate
): string {
  if (template instanceof IncidentTemplate) {
    return 'Incidents'
  }
  if (template instanceof OrderTemplate) {
    return 'Orders'
  }
  return 'Assessments'
}

export function TemplatePage(): JSX.Element {
  const { templateId } = useParams()
  const navigate = useNavigate()

  const { data, isLoading } = useRead(ExtendedLibrary, templateId)
  const template = useMemo(() => {
    const fhirResource = data?.toFhirResource()
    if (NoteTemplate.isInstance(fhirResource)) {
      return NoteTemplate.fromFhirResource(fhirResource)
    }
    if (AssessmentTemplate.isInstance(fhirResource)) {
      return AssessmentTemplate.fromFhirResource(fhirResource)
    }
    if (OrderTemplate.isInstance(fhirResource)) {
      return OrderTemplate.fromFhirResource(fhirResource)
    }
    if (IncidentTemplate.isInstance(fhirResource)) {
      return IncidentTemplate.fromFhirResource(fhirResource)
    }
    return null
  }, [data])

  const templateMutation = usePatchMutation<
    AssessmentTemplate | OrderTemplate | IncidentTemplate
  >()
  const { mutate: patchTemplate } = templateMutation

  const noteTemplateMutation = usePatchMutation<NoteTemplate>()
  const { mutate: patchNoteTemplate } = noteTemplateMutation

  // We need to disable buttons while editing because blurring a section will trigger a mutation which could then cause a race condition with the mutation triggered by the button click.
  const [isEditingSectionId, setIsEditingSectionId] = useState<string | null>(
    null
  )

  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false)

  const openHistoryDialog = useCallback(() => {
    setIsHistoryDialogOpen(true)
  }, [])

  const handleRestoreVersion = useCallback(
    (
      restoredTemplate:
        | NoteTemplate
        | AssessmentTemplate
        | OrderTemplate
        | IncidentTemplate
    ) => {
      if (!template) {
        return
      }

      if (
        restoredTemplate instanceof NoteTemplate &&
        template instanceof NoteTemplate
      ) {
        patchNoteTemplate({
          instance: template,
          mutateFn: (t) => {
            t.data = restoredTemplate.data
          },
        })
      } else if (
        !(restoredTemplate instanceof NoteTemplate) &&
        !(template instanceof NoteTemplate)
      ) {
        patchTemplate({
          instance: template,
          mutateFn: (t) => {
            t.sections = restoredTemplate.sections
          },
        })
      }
    },
    [template, patchTemplate, patchNoteTemplate]
  )

  if (template instanceof NoteTemplate) {
    return (
      <NoteTemplatePage
        template={template}
        onOpenHistoryDialog={openHistoryDialog}
        isHistoryDialogOpen={isHistoryDialogOpen}
        onHistoryDialogOpenChange={setIsHistoryDialogOpen}
        onRestoreVersion={handleRestoreVersion}
      />
    )
  }

  if (isLoading) {
    return <Loader />
  }

  if (!template) {
    return <div>Template not found</div>
  }

  // TODO: Remove model code below (i.e. generateUUID, etc.) from the react package once assessments are based on a single rich text editor.

  const sectionCount = template.sections.length
  const allowNewSection = sectionCount < template.maxSectionsCount
  const emptySectionNumbers = template.sections.flatMap((section, idx) =>
    isDroppableEmptySection(section) ? [idx + 1] : []
  )
  const resourceNounPlural = pluralResourceNoun(template)

  return (
    <PageLayout>
      <div className="pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 gap-1 text-foreground-secondary"
          onClick={() => navigate('/templates')}
        >
          <IconChevronLeft className="size-4" />
          Templates
        </Button>
      </div>
      <div className="mx-auto flex w-full max-w-(--container-prose) flex-col gap-10 pb-16">
        <div className="flex items-center justify-between gap-4">
          <ResourceFieldForm
            instance={template}
            valueFn={(t) => t.title}
            patchFn={(t, value) => {
              t.title = value
            }}
            className="min-w-0 flex-1"
            debounceTyping
            render={({ field }) => (
              <H1Editor placeholder="Template title" {...field} />
            )}
            valueSchema={z
              .string()
              .min(1, { message: 'Title must be at least 1 character' })}
          />
          <MoreDropdownMenu>
            <DropdownMenuItem onSelect={openHistoryDialog}>
              <IconHistory className="size-4" />
              Version history
            </DropdownMenuItem>
            <TemplateDeleteDropdownMenuItem template={template} />
          </MoreDropdownMenu>
        </div>
        {emptySectionNumbers.length > 0 && (
          <div className="sticky top-2 z-10 rounded-md bg-background shadow-sm">
            <div className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <IconAlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" />
              <div className="flex min-w-0 flex-col gap-1">
                <span className="text-sm font-medium text-destructive">
                  {emptySectionNumbers.length === 1
                    ? `Section ${emptySectionNumbers[0]} is empty`
                    : `Sections ${listFormatter.format(emptySectionNumbers.map(String))} are empty`}
                </span>
                <span className="text-xs text-muted-foreground">
                  {resourceNounPlural} cannot be created from this template
                  until empty sections are filled or removed.
                </span>
              </div>
            </div>
          </div>
        )}
        {template.sections.map((section, idx) => (
          <TemplateSection
            key={section.id}
            template={template}
            section={section}
            idx={idx}
            templateMutation={templateMutation}
            isEditingSectionId={isEditingSectionId}
            setIsEditingSectionId={setIsEditingSectionId}
            allowNewSection={allowNewSection}
            emptyPlaceholder={emptySectionPlaceholder(resourceNounPlural)}
          />
        ))}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="gap-2"
            disabled={
              templateMutation.isPending ||
              !!isEditingSectionId ||
              !allowNewSection
            }
            onClick={() => {
              templateMutation.mutate({
                instance: template,
                mutateFn: (t) => {
                  t.sections = [
                    ...t.sections,
                    {
                      id: generateUUID(),
                      text: { div: '', status: 'additional' },
                    },
                  ]
                },
              })
            }}
          >
            <IconPlus className="size-4" />
            Section
          </Button>
          {template.allowValidatedTools && (
            <Combobox
              options={ValidatedTool.buildAll().map((tool) => ({
                label: tool.name,
                value: tool.canonical,
                keywords: [tool.name],
              }))}
              onSelect={(validatedToolCanonical) => {
                templateMutation.mutate({
                  instance: template,
                  mutateFn: (t) => {
                    t.sections = [
                      ...t.sections,
                      {
                        id: generateUUID(),
                        code: codeableConcept({
                          system: codeSystems.ASSESSMENT_SECTION_VALIDATED_TOOL,
                          code: validatedToolCanonical,
                        }),
                        text: {
                          div: validatedToolCanonical,
                          status: 'additional',
                        },
                      },
                    ]
                  },
                })
              }}
            >
              <Button
                className="flex items-center gap-2"
                variant="outline"
                disabled={templateMutation.isPending || !!isEditingSectionId}
              >
                <IconPlus className="size-4" />
                Validated Tool
              </Button>
            </Combobox>
          )}
        </div>
      </div>
      <TemplateHistoryDialog
        open={isHistoryDialogOpen}
        onOpenChange={setIsHistoryDialogOpen}
        templateId={templateId}
        onRestoreVersion={handleRestoreVersion}
      />
    </PageLayout>
  )
}

function TemplateSection<
  Template extends AssessmentTemplate | OrderTemplate | IncidentTemplate,
  TemplateSection extends LibrarySection,
>({
  section,
  idx,
  template,
  templateMutation,
  isEditingSectionId,
  setIsEditingSectionId,
  allowNewSection,
  emptyPlaceholder,
}: {
  section: TemplateSection
  idx: number
  template: Template
  templateMutation: UseMutationResult<
    Template,
    unknown,
    UsePatchMutationProxy<Template>
  >
  isEditingSectionId: string | null
  setIsEditingSectionId: Dispatch<SetStateAction<string | null>>
  allowNewSection: boolean
  emptyPlaceholder: string
}): JSX.Element {
  const editorRef = useRef<Editor>(null)
  const isEmpty = isDroppableEmptySection(section)

  return (
    <div key={section.id}>
      <div className="flex items-center justify-between gap-2">
        <TypographySubtle className="mt-px flex-1">
          Section {idx + 1}
        </TypographySubtle>
        <div className="flex flex-1 items-center justify-end">
          <Button
            variant="ghost"
            className="gap-2 text-foreground-secondary"
            disabled={
              templateMutation.isPending ||
              !!isEditingSectionId ||
              !allowNewSection
            }
            onClick={() => {
              templateMutation.mutate({
                instance: template,
                mutateFn: (t) => {
                  t.sections = [
                    ...t.sections.slice(0, idx),
                    {
                      id: generateUUID(),
                      text: { div: '', status: 'additional' },
                    },
                    ...t.sections.slice(idx),
                  ]
                },
              })
            }}
          >
            <IconPlus size={16} />
            Section
          </Button>
          {template.allowValidatedTools && (
            <Combobox
              options={ValidatedTool.buildAll().map((tool) => ({
                label: tool.name,
                value: tool.canonical,
                keywords: [tool.name],
              }))}
              onSelect={(validatedToolCanonical) => {
                templateMutation.mutate({
                  instance: template,
                  mutateFn: (t) => {
                    t.sections = [
                      ...t.sections.slice(0, idx),
                      {
                        id: generateUUID(),
                        code: codeableConcept({
                          system: codeSystems.ASSESSMENT_SECTION_VALIDATED_TOOL,
                          code: validatedToolCanonical,
                        }),
                        text: {
                          div: validatedToolCanonical,
                          status: 'additional',
                        },
                      },
                      ...t.sections.slice(idx),
                    ]
                  },
                })
              }}
            >
              <Button
                className="flex items-center gap-2 text-foreground-secondary"
                variant="ghost"
                disabled={templateMutation.isPending || !!isEditingSectionId}
              >
                <IconPlus className="size-4" />
                Validated tool
              </Button>
            </Combobox>
          )}
          <Button
            variant="ghost"
            className="gap-2 text-foreground-secondary"
            disabled={
              templateMutation.isPending ||
              (isEditingSectionId !== null && isEditingSectionId !== section.id)
            }
            onClick={() => {
              editorRef.current
                ?.chain()
                .insertContent(
                  "<button data-type='choice' data-options=''></button>"
                )
                .focus()
                .run()
            }}
          >
            <IconPlus className="size-4" />
            Button
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-foreground-secondary"
            disabled={
              templateMutation.isPending || !!isEditingSectionId || idx === 0
            }
            onClick={() => {
              templateMutation.mutate({
                instance: template,
                mutateFn: (t) => {
                  t.sections = [
                    ...t.sections.slice(0, idx - 1),
                    t.sections[idx],
                    t.sections[idx - 1],
                    ...t.sections.slice(idx + 1),
                  ]
                },
              })
            }}
          >
            <IconChevronUp size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-foreground-secondary"
            disabled={
              templateMutation.isPending ||
              !!isEditingSectionId ||
              idx === template.sections.length - 1
            }
            onClick={() => {
              templateMutation.mutate({
                instance: template,
                mutateFn: (t) => {
                  t.sections = [
                    ...t.sections.slice(0, idx),
                    t.sections[idx + 1],
                    t.sections[idx],
                    ...t.sections.slice(idx + 2),
                  ]
                },
              })
            }}
          >
            <IconChevronDown size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-foreground-secondary"
            disabled={templateMutation.isPending || !!isEditingSectionId}
            onClick={() => {
              templateMutation.mutate({
                instance: template,
                mutateFn: (t) => {
                  t.sections = [
                    ...t.sections.slice(0, idx),
                    ...t.sections.slice(idx + 1),
                  ]
                },
              })
            }}
          >
            <IconTrash size={16} />
          </Button>
        </div>
      </div>
      {systemFromCodeableConcept(section.code) ===
      codeSystems.ASSESSMENT_SECTION_VALIDATED_TOOL ? (
        <ValidatedToolSectionPreview
          key={section.id ?? idx}
          section={section}
        />
      ) : (
        <ResourceFieldForm
          key={section.id ?? idx}
          instance={template}
          valueFn={(t) => t.sections[idx].text?.div ?? ''}
          patchFn={(t, html) => {
            t.sections = [
              ...t.sections.slice(0, idx),
              {
                id: t.sections[idx].id,
                text: { div: html, status: 'additional' },
              },
              ...t.sections.slice(idx + 1),
            ]
          }}
          debounceTyping
          render={({ field: { onBlur, ...rest } }) => (
            <div className="relative">
              <RichTextEditor
                editorRef={editorRef}
                choiceNodesEditable
                enableColumns
                isTemplate
                contentClassName={cn(
                  'p-10',
                  isEmpty && 'border-destructive/50 bg-destructive/5'
                )}
                onFocus={() => setIsEditingSectionId(section.id)}
                onBlur={() => {
                  onBlur()
                  setIsEditingSectionId(null)
                }}
                {...rest}
              />
              {isEmpty && (
                <div className="pointer-events-none absolute top-10 right-10 left-10 text-sm text-destructive/70">
                  {emptyPlaceholder}
                </div>
              )}
            </div>
          )}
        />
      )}
    </div>
  )
}

function NoteTemplatePage({
  template,
  onOpenHistoryDialog,
  isHistoryDialogOpen,
  onHistoryDialogOpenChange,
  onRestoreVersion,
}: {
  template: NoteTemplate
  onOpenHistoryDialog: () => void
  isHistoryDialogOpen: boolean
  onHistoryDialogOpenChange: (open: boolean) => void
  onRestoreVersion: (
    template:
      | NoteTemplate
      | AssessmentTemplate
      | OrderTemplate
      | IncidentTemplate
  ) => void
}): JSX.Element {
  const navigate = useNavigate()
  // The field panel opens in this column beside the page, which narrows to
  // make room.
  const [panelSlot, setPanelSlot] = useState<HTMLElement | null>(null)
  return (
    <NodeConfigPanelSlotContext.Provider value={panelSlot}>
      <div className="flex size-full min-h-0">
        <div className="min-h-0 min-w-0 flex-1">
          {/* `bg-muted` on the ScrollArea root (via `className`) backs the
           * full viewport height; `contentClassName` keeps the scrollable
           * content muted too, so there is no white gap below short
           * templates. */}
          <PageLayout className="bg-muted" contentClassName="bg-muted">
            <div className="pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="-ml-2 gap-1 text-foreground-secondary"
                onClick={() => navigate('/templates')}
              >
                <IconChevronLeft className="size-4" />
                Templates
              </Button>
            </div>
            <div className="mx-auto flex w-full max-w-(--container-prose) flex-col gap-8 pb-16">
              <div className="flex items-center justify-between gap-4">
                <ResourceFieldForm
                  instance={template}
                  valueFn={(t) => t.title}
                  patchFn={(t, value) => {
                    t.title = value
                  }}
                  className="min-w-0 flex-1"
                  debounceTyping
                  render={({ field }) => (
                    <H1Editor placeholder="Template title" {...field} />
                  )}
                  valueSchema={z
                    .string()
                    .min(1, { message: 'Title must be at least 1 character' })}
                />
                <MoreDropdownMenu>
                  <DropdownMenuItem onSelect={onOpenHistoryDialog}>
                    <IconHistory className="size-4" />
                    Version history
                  </DropdownMenuItem>
                  <DropdownMenuMedplumLinkItem to={template} />
                  <TemplateDeleteDropdownMenuItem template={template} />
                </MoreDropdownMenu>
              </div>
              {/* Default note properties, sitting between the title and the body. The
               * same picker as the note header: labels persist to `meta.tag`,
               * categories to `Library.topic[]`; `patchFn` splits the combined value
               * back by predicate. These merge into a note when this template is
               * instantiated or @-mention-inserted, and stay editable on the note. */}
              <div className="flex flex-col items-start gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-foreground">
                    Default labels
                  </span>
                  <span className="text-sm text-muted-foreground">
                    Automatically applied to notes using this template, and
                    editable on the note.
                  </span>
                </div>
                <ResourceFieldForm
                  instance={template}
                  valueFn={(t) => [...t.labels, ...t.categories]}
                  patchFn={(t, value) => {
                    const { labels, categories } = partitionLabelSelection(
                      value as string[]
                    )
                    t.labels = labels
                    t.categories = categories
                  }}
                  render={({ field }) => (
                    <NoteLabelComboboxButton {...field} includeCategories />
                  )}
                />
              </div>
              <div className="rounded-lg border bg-background shadow-xs">
                <ResourceFieldForm
                  instance={template}
                  valueFn={(t) => t.data}
                  patchFn={(t, html) => {
                    /* Create a durable opaque fieldId on every choice field that lacks
                     * one before persisting the template body, so the AI can target
                     * choice fields by stable id (idempotent — existing ids are kept).
                     * Template saves only; note saves are untouched, and choice fields
                     * added directly to a note without an id are simply un-targetable. */
                    t.data = createChoiceFieldIds(html)
                  }}
                  debounceTyping
                  render={({ field }) => (
                    <RichTextEditor
                      choiceNodesEditable
                      enableColumns
                      isTemplate
                      contentClassName="px-8 pt-8 pb-12 min-h-64"
                      variant="unstyled"
                      placeholder="Write, press '@' for commands…"
                      {...field}
                    />
                  )}
                />
              </div>
            </div>
            <TemplateHistoryDialog
              open={isHistoryDialogOpen}
              onOpenChange={onHistoryDialogOpenChange}
              templateId={template.id}
              onRestoreVersion={onRestoreVersion}
            />
          </PageLayout>
        </div>
        <div ref={setPanelSlot} className="shrink-0 empty:hidden" />
      </div>
    </NodeConfigPanelSlotContext.Provider>
  )
}
